/**
 * Página de inicio de sesión
 * 
 * Permite a los usuarios autenticarse para acceder al sistema.
 * 
 * @module pages/auth/Login
 * @requires react, react-router-dom, ../../contexts/AuthContext
 * @related_files ../../services/auth.service.js
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaAndroid, FaBuilding, FaHeadset, FaLock, FaShoppingCart, FaSignInAlt, FaUser } from 'react-icons/fa';
import { signInWithCustomToken } from 'firebase/auth';
import { rotateSessionId } from '../../utils/sessionControl';

// Hooks
import { useAuth } from '../../contexts/AuthContext';
import configuracionService from '../../services/configuracion.service';
import { ACCESS_MODES, getStoredAccessMode, canAccessAdminPanel, setStoredAccessMode } from '../../utils/runtimeAccessMode';
import { auth } from '../../firebase/config';
import { getCloudApiBaseUrl } from '../../config/cloudApi';

// Componentes
import Button from '../../components/common/Button';
import PasswordInput from '../../components/common/PasswordInput';
import BrandLogo from '../../components/common/BrandLogo';
import LoginPlanSideCard from '../../components/auth/LoginPlanSideCard';
import LoginPlansMobileStrip from '../../components/auth/LoginPlansMobileStrip';
import LoginPlansCenterTeaser from '../../components/auth/LoginPlansCenterTeaser';
import PlanesAbonoExplainerModal from '../../components/configuracion/PlanesAbonoExplainerModal';

const LOGIN_BG_SRC = `${process.env.PUBLIC_URL || ''}/assets/brand/0e3_pos/login-bg.webp`;
const DEFAULT_CAJA_APK_URL = 'https://nexopos-dc.web.app/app-caja.apk';
const TEAMVIEWER_QS_ANDROID_INTENT =
  'intent://start#Intent;scheme=teamviewerqs;package=com.teamviewer.quicksupport.market;end';
const TEAMVIEWER_QS_PLAYSTORE_URL =
  'https://play.google.com/store/apps/details?id=com.teamviewer.quicksupport.market';
const ADMIN_WEB_URL = 'https://nexopos-dc.web.app/';

const normalizeDemoPhoneDigits = (raw) => String(raw || '').replace(/\D/g, '');
const looksLikeDemoPhone = (raw) => {
  const digits = normalizeDemoPhoneDigits(raw);
  return digits.length >= 10 && digits.length <= 15;
};
const demoEmailFromPhone = (raw) => `demo_${normalizeDemoPhoneDigits(raw)}@nexopos.demo.local`;

const normalizeExternalUrl = (raw) => {
  const value = String(raw || '').trim();
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  if (/^\/\//.test(value)) return `https:${value}`;
  return `https://${value}`;
};

const isNativeCapacitorRuntime = () => {
  try {
    if (typeof window === 'undefined') return false;
    if (window?.NexoAndroid) return true;
    if (
      !!window.Capacitor &&
      typeof window.Capacitor.isNativePlatform === 'function' &&
      window.Capacitor.isNativePlatform()
    ) {
      return true;
    }
    const ua = String(window?.navigator?.userAgent || '').toLowerCase();
    const isAndroidWebView = ua.includes('android') && (ua.includes('; wv') || ua.includes('version/'));
    return isAndroidWebView;
  } catch {
    return false;
  }
};

/**
 * Componente de página de inicio de sesión
 * @returns {JSX.Element} Componente Login
 */
const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, currentUser } = useAuth();
  const nativeRuntime = isNativeCapacitorRuntime();
  const initialAccessMode = (() => {
    const fromState = location.state?.accessMode;
    const stored = getStoredAccessMode(nativeRuntime ? ACCESS_MODES.CAJERO : ACCESS_MODES.ADMIN);
    if (fromState === ACCESS_MODES.ADMIN || fromState === ACCESS_MODES.CAJERO) return fromState;
    if (location.state?.from?.pathname === '/cajero') return ACCESS_MODES.CAJERO;
    return stored;
  })();
  const [accessMode, setAccessMode] = useState(initialAccessMode);
  
  // Estado del formulario
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [cajaApkUrlServidor, setCajaApkUrlServidor] = useState('');
  const [androidVersionLabel, setAndroidVersionLabel] = useState('');
  const [webBuildLabel, setWebBuildLabel] = useState('');
  const [showPlansModal, setShowPlansModal] = useState(false);
  const [plansModalTab, setPlansModalTab] = useState('intermediate');
  const redirectHandledRef = useRef(false);
  const bridgeLoginHandledRef = useRef(false);

  const openPlansModal = useCallback((planId = 'intermediate') => {
    setPlansModalTab(planId);
    setShowPlansModal(true);
  }, []);

  const envCajaApkUrl = (process.env.REACT_APP_CAJA_APK_URL || '').trim();
  const serverCajaApkUrl = normalizeExternalUrl(cajaApkUrlServidor || '');
  const envCajaApkUrlNormalized = normalizeExternalUrl(envCajaApkUrl || '');
  const isOfficialHostedApk = (url) =>
    /^https:\/\/nexopos-dc\.web\.app\/app-caja\.apk([?#].*)?$/i.test(String(url || '').trim());
  const urlDescargaApk = (() => {
    if (isOfficialHostedApk(serverCajaApkUrl)) return serverCajaApkUrl;
    if (isOfficialHostedApk(envCajaApkUrlNormalized)) return envCajaApkUrlNormalized;
    return DEFAULT_CAJA_APK_URL;
  })();
  const isAndroidWeb = (() => {
    try {
      const ua = String(window?.navigator?.userAgent || '').toLowerCase();
      return ua.includes('android') && !nativeRuntime;
    } catch {
      return false;
    }
  })();

  const loginBackgroundStyle = {
    backgroundColor: '#0e0e11',
    backgroundImage: `url(${LOGIN_BG_SRC})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat'
  };

  const isAdminAuthorized = useCallback((user) => {
    return canAccessAdminPanel(user);
  }, []);

  const extractBuildFromAsset = useCallback((mainJsPath) => {
    const value = String(mainJsPath || '');
    const match = value.match(/main\.([a-z0-9]+)\.js$/i);
    return match?.[1] || '';
  }, []);

  const buildFreshAdminUrl = useCallback(() => {
    return `${ADMIN_WEB_URL}?nexoFresh=${Date.now()}&source=android-admin`;
  }, []);

  const requestAdminBridgeUrl = useCallback(async () => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) return '';
    const token = await firebaseUser.getIdToken(true);
    const apiBase = getCloudApiBaseUrl();
    const response = await fetch(`${apiBase}/auth/admin-bridge-url`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload?.message || 'No se pudo preparar acceso admin');
    }
    return String(payload?.data?.url || '').trim();
  }, []);

  const redirectAfterLogin = useCallback(async (user) => {
    redirectHandledRef.current = true;
    const quiereAdmin = accessMode === ACCESS_MODES.ADMIN;
    if (quiereAdmin) {
      if (!isAdminAuthorized(user)) {
        setStoredAccessMode(ACCESS_MODES.CAJERO);
        toast.warning('Este usuario solo tiene acceso de cajero (mostrador).');
        navigate('/cajero', { replace: true });
        return;
      }
      if (nativeRuntime) {
        // En Android evitamos cargar admin embebido mientras se valida PIN en el bridge nativo.
        setStoredAccessMode(ACCESS_MODES.CAJERO);
        navigate('/cajero', { replace: true });
        let adminUrl = buildFreshAdminUrl();
        try {
          const bridgedUrl = await requestAdminBridgeUrl();
          if (bridgedUrl) adminUrl = bridgedUrl;
        } catch (error) {
          console.warn('No se pudo obtener URL de puente admin:', error);
        }
        try {
          const bridge = window?.NexoAndroid;
          if (bridge && typeof bridge.openAdminInChromePrivateUrl === 'function') {
            bridge.openAdminInChromePrivateUrl(adminUrl);
            toast.info('Administrador abierto en navegador privado.');
            return;
          }
          if (bridge && typeof bridge.openAdminInChromePrivate === 'function') {
            bridge.openAdminInChromePrivate();
            toast.info('Administrador abierto en navegador privado.');
            return;
          }
          if (bridge && typeof bridge.openAdminInChrome === 'function') {
            bridge.openAdminInChrome();
            toast.info('Administrador abierto en navegador.');
            return;
          }
        } catch (error) {
          console.warn('No se pudo abrir admin en navegador nativo:', error);
        }
        try {
          window.location.assign(adminUrl);
        } catch {
          window.open(adminUrl, '_blank', 'noopener,noreferrer');
        }
        return;
      }
      setStoredAccessMode(ACCESS_MODES.ADMIN);
      navigate('/', { replace: true });
      return;
    }
    setStoredAccessMode(ACCESS_MODES.CAJERO);
    navigate('/cajero', { replace: true });
  }, [accessMode, buildFreshAdminUrl, isAdminAuthorized, navigate, nativeRuntime, requestAdminBridgeUrl]);

  const handleDescargarApk = (event) => {
    event?.preventDefault?.();
    if (!urlDescargaApk) {
      toast.warning('No hay URL de descarga configurada.');
      return;
    }

    const directApk = /\.apk([?#].*)?$/i.test(urlDescargaApk);
    if (!directApk) {
      toast.info(
        'El enlace configurado no parece un archivo .apk directo. En Android, si apunta a una página intermedia (Drive/preview), la descarga puede fallar.',
        { autoClose: 6500 }
      );
    }

    // En Android Web funciona mejor descargar en la misma pestaña.
    try {
      if (isAndroidWeb) {
        window.location.assign(urlDescargaApk);
        return;
      }
      window.open(urlDescargaApk, '_blank', 'noopener,noreferrer');
    } catch {
      window.location.href = urlDescargaApk;
    }
  };

  const handleAbrirSoporteRemoto = (event) => {
    event?.preventDefault?.();
    try {
      if (nativeRuntime) {
        const bridge = window?.NexoAndroid;
        if (bridge && typeof bridge.openTeamViewerQuickSupport === 'function') {
          bridge.openTeamViewerQuickSupport();
          return;
        }
      }

      if (isAndroidWeb) {
        window.location.assign(TEAMVIEWER_QS_ANDROID_INTENT);
        return;
      }

      window.open(TEAMVIEWER_QS_PLAYSTORE_URL, '_blank', 'noopener,noreferrer');
    } catch {
      window.location.href = TEAMVIEWER_QS_PLAYSTORE_URL;
    }
  };

  useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        const raw = await configuracionService.obtenerConfiguracionEmpresa();
        const u = typeof raw?.caja_apk_url === 'string' ? raw.caja_apk_url.trim() : '';
        if (!cancelado && u) setCajaApkUrlServidor(u);
      } catch {
        /* sin URL desde API */
      }
    })();
    return () => { cancelado = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const assetManifestUrl = `${window.location.origin}/asset-manifest.json?nexoFresh=${Date.now()}`;
        const response = await fetch(assetManifestUrl, { cache: 'no-store' });
        if (!response.ok) return;
        const manifest = await response.json();
        if (cancelled) return;
        const mainJs = manifest?.files?.['main.js'] || manifest?.files?.main || '';
        const buildId = extractBuildFromAsset(mainJs);
        setWebBuildLabel(buildId || 'sin-hash');
      } catch {
        if (!cancelled) setWebBuildLabel('no-disponible');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [extractBuildFromAsset]);

  useEffect(() => {
    if (!nativeRuntime) {
      setAndroidVersionLabel('web');
      return;
    }
    try {
      const bridge = window?.NexoAndroid;
      const versionName = bridge && typeof bridge.getInstalledVersionName === 'function'
        ? String(bridge.getInstalledVersionName() || '').trim()
        : '';
      const versionCode = bridge && typeof bridge.getInstalledVersionCode === 'function'
        ? Number(bridge.getInstalledVersionCode() || 0)
        : 0;
      if (versionName || versionCode > 0) {
        setAndroidVersionLabel(`${versionName || 's/n'} (${versionCode || 0})`);
        return;
      }
      setAndroidVersionLabel('no-disponible');
    } catch {
      setAndroidVersionLabel('no-disponible');
    }
  }, [nativeRuntime]);

  useEffect(() => {
    if (bridgeLoginHandledRef.current) return;
    const params = new URLSearchParams(location.search || '');
    const bridgeFlag = params.get('adminBridge');
    const customToken = params.get('customToken');
    if (bridgeFlag !== '1' || !customToken) return;

    bridgeLoginHandledRef.current = true;
    (async () => {
      try {
        setLoading(true);
        redirectHandledRef.current = true;
        rotateSessionId();
        await signInWithCustomToken(auth, customToken);
        setStoredAccessMode(ACCESS_MODES.ADMIN);
        const nextPath = params.get('next') || '/';
        navigate(nextPath, { replace: true });
      } catch (error) {
        console.error('Error en autologin admin bridge:', error);
        toast.error('No se pudo iniciar sesión automática en el panel.');
        navigate('/login', { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [location.search, navigate]);
  
  // Mantener pantalla de login al abrir la app.
  // La redirección ocurre solo después de un submit exitoso (handleSubmit -> redirectAfterLogin).
  useEffect(() => {
    if (!isAuthenticated) {
      redirectHandledRef.current = false;
      return;
    }
    if (!currentUser) return;
    if (redirectHandledRef.current) return;
    redirectHandledRef.current = true;

    const wantsAdmin = accessMode === ACCESS_MODES.ADMIN;
    if (wantsAdmin && isAdminAuthorized(currentUser)) {
      if (nativeRuntime) {
        setStoredAccessMode(ACCESS_MODES.CAJERO);
        navigate('/cajero', { replace: true });
        return;
      }
      setStoredAccessMode(ACCESS_MODES.ADMIN);
      navigate('/', { replace: true });
      return;
    }
    setStoredAccessMode(ACCESS_MODES.CAJERO);
    navigate('/cajero', { replace: true });
  }, [accessMode, currentUser, isAdminAuthorized, isAuthenticated, navigate, nativeRuntime]);
  
  /**
   * Actualiza el estado del formulario
   * @param {Event} e - Evento de cambio
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Limpiar error al cambiar el valor
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  
  /**
   * Valida el formulario antes de enviar
   * @returns {boolean} True si es válido
   */
  const validateForm = () => {
    const newErrors = {};

    const inputLogin = String(formData.email || '').trim();
    const emailOk = /\S+@\S+\.\S+/.test(inputLogin);
    const phoneOk = looksLikeDemoPhone(inputLogin);

    if (!formData.email) {
      newErrors.email = 'El correo electrónico o celular es obligatorio';
    } else if (!emailOk && !phoneOk) {
      newErrors.email = 'Ingresá un email válido o un celular (10 a 15 dígitos) para demo';
    }
    
    if (!formData.password) {
      newErrors.password = 'La contraseña es obligatoria';
    }
    
    setErrors(newErrors);
    
    return Object.keys(newErrors).length === 0;
  };
  
  /**
   * Maneja el envío del formulario
   * @param {Event} e - Evento de envío
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Evita redirecciones automáticas prematuras durante el login.
      redirectHandledRef.current = true;
      const inputLogin = String(formData.email || '').trim();
      const usingDemoPhone = looksLikeDemoPhone(inputLogin) && !inputLogin.includes('@');
      const loginEmail = usingDemoPhone ? demoEmailFromPhone(inputLogin) : inputLogin;
      const user = await login(loginEmail, formData.password);
      await redirectAfterLogin(user);
    } catch (error) {
      redirectHandledRef.current = false;
      console.error('Error al iniciar sesión:', error);
      
      const errorMsg = error.response?.data?.message || 'Error al iniciar sesión';
      toast.error(errorMsg);
      
      // Si es error de credenciales, marcar ambos campos
      if (error.response?.status === 401) {
        setErrors({
          email: 'Credenciales incorrectas',
          password: 'Credenciales incorrectas'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="login-page relative flex min-h-0 flex-1 flex-col overflow-x-hidden lg:overflow-hidden"
      style={loginBackgroundStyle}
    >
      <div className="login-page__scrim absolute inset-0 bg-e0e3-bg/75" aria-hidden />

      <div className="relative z-10 flex min-h-0 flex-1 flex-col items-center justify-center px-3 py-3 sm:px-4 lg:py-2">
        <div className="grid w-full max-w-6xl grid-cols-1 items-stretch gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(340px,420px)_minmax(0,1fr)] lg:gap-4 lg:max-h-[calc(100vh-5.5rem)]">
          <LoginPlanSideCard planId="basic" onVerPlan={openPlansModal} />

          <div className="flex min-h-0 flex-col overflow-hidden rounded-2xl border border-white/12 bg-e0e3-surface/95 shadow-elevated ring-1 ring-white/10 backdrop-blur-md">
            <div className="h-1 w-full shrink-0 bg-gradient-to-r from-transparent via-e0e3-accent to-e0e3-accentSoft" />
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5 sm:px-6 sm:py-5 lg:overflow-visible lg:py-4">
              <div className="mb-4 flex flex-col items-center text-center">
                <div className="mb-3 rounded-xl bg-black/80 px-4 py-2.5 ring-1 ring-white/[0.12]">
                  <BrandLogo
                    className="flex h-14 w-auto items-center justify-center sm:h-16"
                    showText={false}
                    variant="full"
                  />
                </div>
                <h1 className="text-2xl font-extrabold tracking-tight text-e0e3-foreground sm:text-[1.65rem]">
                  Ingresar a 0E3 POS
                </h1>
                <p className="mt-1 text-xs font-medium text-e0e3-accentSoft sm:text-sm">
                  Tecnología que simplifica
                </p>
              </div>

              <LoginPlansMobileStrip onVerPlan={openPlansModal} />

              <div className="mb-4 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setAccessMode(ACCESS_MODES.ADMIN)}
                  className={`rounded-xl border-2 p-3 text-left transition ${
                    accessMode === ACCESS_MODES.ADMIN
                      ? 'border-e0e3-accent bg-e0e3-accent/[0.12] text-e0e3-accent shadow-[0_0_22px_-6px_rgba(30,144,255,0.55)]'
                      : 'border-white/15 bg-black/35 text-e0e3-muted'
                  }`}
                >
                  <FaBuilding className="mb-1.5 text-xl" />
                  <div className="text-sm font-bold">Administrador</div>
                  <div className="mt-0.5 text-[11px]">Panel completo</div>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAccessMode(ACCESS_MODES.CAJERO);
                    setStoredAccessMode(ACCESS_MODES.CAJERO);
                  }}
                  className={`rounded-xl border-2 p-3 text-left transition ${
                    accessMode === ACCESS_MODES.CAJERO
                      ? 'border-emerald-400/85 bg-emerald-500/[0.12] text-emerald-100 shadow-[0_0_22px_-6px_rgba(16,185,129,0.35)]'
                      : 'border-white/15 bg-black/35 text-e0e3-muted'
                  }`}
                >
                  <FaShoppingCart className="mb-1.5 text-xl" />
                  <div className="text-sm font-bold">Cajero</div>
                  <div className="mt-0.5 text-[11px]">Mostrador/POS</div>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
            {/* Correo electrónico */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-e0e3-muted">
                Correo electrónico o celular (demo)
              </label>
              <div className="relative mt-1">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <FaUser className="h-5 w-5 text-e0e3-muted" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="text"
                  autoComplete="username"
                  value={formData.email}
                  onChange={handleChange}
                  className={`nexo-field pl-10 ${errors.email ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' : ''}`}
                  placeholder="ejemplo@correo.com o 3764123456"
                />
              </div>
              {errors.email && (
                <p className="mt-2 text-sm text-red-400">{errors.email}</p>
              )}
            </div>
            
            {/* Contraseña */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-e0e3-muted">
                Contraseña
              </label>
              <div className="mt-1">
                <PasswordInput
                  id="password"
                  name="password"
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`nexo-field ${errors.password ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' : ''}`}
                  placeholder="••••••••"
                  leftSlot={<FaLock className="h-5 w-5 text-e0e3-muted" />}
                />
              </div>
              {errors.password && (
                <p className="mt-2 text-sm text-red-400">{errors.password}</p>
              )}
            </div>
            
            <div>
              <Button
                type="submit"
                color="primary"
                fullWidth
                loading={loading}
                icon={<FaSignInAlt />}
              >
                {accessMode === ACCESS_MODES.CAJERO ? 'Ingresar al mostrador' : 'Ingresar al panel'}
              </Button>
            </div>
              </form>

              <LoginPlansCenterTeaser onVerPlan={openPlansModal} onVerAll={() => openPlansModal('intermediate')} />

              <div className="mt-3 space-y-2 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-3">
                <p className="text-xs font-semibold text-e0e3-foreground">¿Sin cuenta?</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  <Link
                    to="/signup"
                    className="rounded-xl border border-sky-400/35 bg-sky-500/10 px-3 py-2.5 text-left transition hover:bg-sky-500/15"
                  >
                    <span className="block text-xs font-bold text-sky-100">Crear mi empresa</span>
                    <span className="mt-1 block text-[11px] leading-snug text-e0e3-muted">
                      Creá tu empresa real y usá todas las funciones durante 30 días. Tus datos permanecerán
                      guardados cuando actives el plan.
                    </span>
                  </Link>
                  <Link
                    to="/signup?mode=demo"
                    state={{ signupMode: 'demo' }}
                    className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.07] px-3 py-2.5 text-left transition hover:bg-emerald-500/15"
                  >
                    <span className="block text-xs font-bold text-emerald-100">Probar demo</span>
                    <span className="mt-1 block text-[11px] leading-snug text-e0e3-muted">
                      Explorá 0E3 POS durante 48 horas con datos de ejemplo precargados.
                    </span>
                  </Link>
                </div>
              </div>

              <p className="mt-2 text-center text-[11px] text-e0e3-muted/90">
                ¿Olvidaste tu contraseña? Contactá al administrador
              </p>
            </div>
          </div>

          <LoginPlanSideCard planId="premium" onVerPlan={openPlansModal} />
        </div>
      </div>

      <footer className="relative z-10 shrink-0 border-t border-white/10 bg-black/50 px-3 py-2.5 backdrop-blur-sm sm:px-6">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-2 sm:flex-row sm:justify-between">
          <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-start">
            {urlDescargaApk ? (
              <a
                href={urlDescargaApk}
                onClick={handleDescargarApk}
                target={isAndroidWeb ? '_self' : '_blank'}
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/40 bg-emerald-600/90 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-500"
              >
                <FaAndroid className="text-sm" />
                App Caja (APK)
              </a>
            ) : null}
            <button
              type="button"
              onClick={handleAbrirSoporteRemoto}
              className="inline-flex items-center gap-1.5 rounded-lg border border-e0e3-accent/40 bg-e0e3-accent/15 px-3 py-1.5 text-xs font-semibold text-e0e3-accent-soft hover:bg-e0e3-accent/25"
            >
              <FaHeadset className="text-sm" />
              Soporte remoto
            </button>
          </div>
          <div className="text-center text-[11px] text-e0e3-muted sm:text-right">
            <span>
              v{androidVersionLabel || '…'} · build {webBuildLabel || '…'}
            </span>
            <span className="mx-1 hidden sm:inline">·</span>
            <span className="block sm:inline">© 2026 0E3 POS</span>
          </div>
        </div>
      </footer>

      <PlanesAbonoExplainerModal
        open={showPlansModal}
        onClose={() => setShowPlansModal(false)}
        initialPlan={plansModalTab}
      />
    </div>
  );
};

export default Login;